import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import classNames from 'classnames';
import Button from '../form/Button';

interface Item {
  name: React.ReactNode | string | number;
  link?: string;
  onClick?: () => void;
}

interface DropdownGenericProps {
  title: React.ReactNode | string;
  items: Item[];
  isDark?: boolean;
}

export default function DropdownGeneric({ title, items, isDark }: DropdownGenericProps) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleItemClick = (item: Item) => {
    if (item.onClick) {
      item.onClick();
    } else if (item.link) {
      window.open(item.link, '_self');
    }
    setOpen(false);
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <Button
        className={classNames(isDark ? "" : "!text-white hover:!text-primary active:!text-primary focus:!text-primary")}
        onClick={() => setOpen(!open)}
        variant="transparent"
        size="wide"
        endIcon={<i className={`gg-chevron-down`} />}
      >
        {title}
      </Button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            className="absolute left-0 top-full mt-2 bg-white rounded-lg shadow-lg p-4 z-50 min-w-[220px]"
          >
            <ul className="space-y-1">
              {items.map((it, idx) => (
                <li
                  key={it.link ?? String(it.name) ?? idx}
                  className="px-3 py-2 rounded cursor-pointer hover:bg-gray-100 text-[#212121]"
                  onClick={() => handleItemClick(it)}
                >
                  {it.name}
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
